import React, { useEffect, useState } from 'react'
import {
	Box,
	CircularProgress,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import DownloadCsvButton from './DownloadCsvButton'
import { dsColorNew, dataColors } from '@palette'

/**
 * Revenue breakdown by Service Type (Rent / Insurance / Fuel / Damage /
 * Extras) — Thibault's "Net vs Gross" matrix.
 *
 *   <ServiceTypeBreakdown from="2026-01-01" to="2026-06-14"
 *                         offices={['A1']} categories={['ECMR']}
 *                         duration="total"/>
 *
 * Backend: GET /api/my-business/revenue/service-type
 */
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 0, maximumFractionDigits: 0,
	}).format(n || 0)
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n || 0)

const BUCKET_COLOR = {
	Rent:      dataColors.sky500,
	Insurance: dataColors.green600,
	Fuel:      dsColorNew.warningBar,
	Damage:    dsColorNew.dangerRed,
	Extras:    dataColors.violet600,
}

export default function ServiceTypeBreakdown({
	from, to, offices = [], categories = [], duration = 'total',
}) {
	const [data,    setData]    = useState(null)
	const [error,   setError]   = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (duration && duration !== 'total') qs.set('duration', duration)
		fetch(`/api/my-business/revenue/service-type?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','), duration])

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Service-type breakdown unreachable: {error}</AppNote>
		</Paper>
	)

	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={6}/>

	const totals = data.totals || {}

	return (
		<Paper sx={{ p: 2 }}>
			<Box sx={{ height: 4, mb: 1 }}>
			</Box>
			<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
				<AppCardTitle icon="layer-group" title="Revenue Breakdown — Net vs Gross"/>
				<DownloadCsvButton
					href={(() => {
						const qs = new URLSearchParams({ from, to })
						if (offices.length)    qs.set('offices', offices.join(','))
						if (categories.length) qs.set('sipps',   categories.join(','))
						if (duration && duration !== 'total') qs.set('duration', duration)
						return `/api/my-business/revenue/items/download?${qs}`
					})()}
					from={from} to={to}
					label="Items CSV"/>
			</Box>

			<TableContainer sx={{ mt: 1 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Bucket</TableCell>
							<TableCell align="right">Bookings</TableCell>
							<TableCell align="right">Net</TableCell>
							<TableCell align="right">Gross</TableCell>
							<TableCell align="right">VAT</TableCell>
							<TableCell>Share (Gross)</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.rows.map((r) => (
							<TableRow key={r.bucket} hover>
								<TableCell>
									<Box display="flex" alignItems="center" gap={1}>
										<Box sx={{ width: 10, height: 10, borderRadius: '2px',
										           bgcolor: BUCKET_COLOR[r.bucket] || dataColors.slate400 }}/>
										<Box sx={{ fontWeight: 600 }}>{r.bucket}</Box>
									</Box>
								</TableCell>
								<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
									{fmtNum(r.bookings)}
								</TableCell>
								<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
									{fmtEur(r.net)}
								</TableCell>
								<TableCell align="right"
								           sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
									{fmtEur(r.gross)}
								</TableCell>
								<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
									{fmtEur(r.vat)}
								</TableCell>
								<TableCell sx={{ width: 220 }}>
									<Box display="flex" alignItems="center" gap={1}>
										<Box sx={{ flex: 1, height: 8, borderRadius: 1,
										           bgcolor: dsColorNew.pillTrackBg, overflow: 'hidden' }}>
											<Box sx={{
												width: `${r.share_gross_pct}%`,
												height: '100%',
												bgcolor: BUCKET_COLOR[r.bucket] || dataColors.slate400,
												transition: 'width 200ms ease',
											}}/>
										</Box>
										<Box sx={{ width: 48, textAlign: 'right',
										           fontVariantNumeric: 'tabular-nums',
										           fontSize: 13 }}>
											{r.share_gross_pct.toFixed(1)}%
										</Box>
									</Box>
								</TableCell>
							</TableRow>
						))}
						<TableRow sx={{ borderTop: 2, borderColor: dsColorNew.textDark }}>
							<TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
							<TableCell/>
							<TableCell align="right"
							           sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
								{fmtEur(totals.net)}
							</TableCell>
							<TableCell align="right"
							           sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
								{fmtEur(totals.gross)}
							</TableCell>
							<TableCell align="right"
							           sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
								{fmtEur(totals.vat)}
							</TableCell>
							<TableCell/>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>

			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.5 }}>
				Buckets follow Thibault's PowerBI mapping:
				Rent = ServiceTypeId 1 · Insurance = 7 · Fuel = 6, 17, 20 ·
				Damage = 5, 16 · Extras = ids 2-4, 8-15, 19, 21.
				Rows synced before the id was persisted fall back to a
				text heuristic on <code>service_type</code>; anything still
				not recognised lands in <strong>Unmapped</strong>.
			</Typography>

			{data.unmapped_samples?.length > 0 && (
				<AppNote severity="warning" sx={{ mt: 1 }}>
					<Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>
						Top unmapped <code>service_type</code> values
					</Typography>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mb: 0.5 }}>
						Tell the team which bucket each of these should land in and
						the mapping is one CASE-WHEN away from being fixed.
					</Typography>
					<Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13 }}>
						{data.unmapped_samples.map((s, i) => (
							<li key={i}>
								<code>{s.service_type}</code> · {fmtNum(s.rows)} rows · {fmtEur(s.gross)}
							</li>
						))}
					</Box>
				</AppNote>
			)}
		</Paper>
	)
}
